import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import {
  addDays,
  formatAmount,
  formatDate,
  formatHKD,
  isMeaningfulInvoice,
  majorToMinor,
  nextInvoiceNumber,
  normalizeInvoice,
  statusLabel,
} from "./calculations";
import {
  deleteCloudClient,
  deleteCloudInvoice,
  deleteCloudProfile,
  deleteCloudReceipt,
  loadCloudWorkspace,
  saveCloudClient,
  saveCloudInvoice,
  saveCloudProfile,
  suppressCloudHistory,
  uploadCloudReceipt,
} from "./data-service";
import {
  exportInvoicePdf,
  exportInvoicePngPages,
  invoiceFilename,
  invoiceSummary,
  waitForExportImages,
} from "./export";
import { InvoicePreview } from "./InvoicePreview";
import { MoneyInput } from "./MoneyInput";
import {
  createBlankInvoice,
  createId,
  createLineItem,
  createProfile,
  profileIssuer,
} from "./invoice-factory";
import {
  loadLocalWorkspace,
  mergeById,
  saveLocalWorkspace,
} from "./local-store";
import {
  profileInitials,
  resolveProfileLogoUrls,
} from "./profile-logos";
import { resolveReceiptImageUrls } from "./receipt-images";
import {
  createSerializedSaveQueue,
  formatSavedTime,
  type SerializedSaveQueue,
} from "./save-queue";
import { supabase } from "./supabase";
import type {
  BillingProfile,
  Client,
  DrawerName,
  Invoice,
  InvoiceStatus,
  PartySnapshot,
  ReceiptAttachment,
  ReceiptMimeType,
} from "./types";

interface WorkspaceProps {
  session: Session | null;
  localMode?: boolean;
}

type SaveState =
  | "LOCAL ONLY"
  | "UNSAVED"
  | "SAVING…"
  | "SAVED"
  | "SYNC ERROR";
type SaveSource = "auto" | "manual";

const LOCAL_USER_ID = "00000000-0000-4000-8000-000000000001";
const MAX_RECEIPTS_PER_INVOICE = 20;
const MAX_RECEIPT_BYTES = 10_000_000;

function supportedReceiptMimeType(file: File): ReceiptMimeType | null {
  return file.type === "image/jpeg" ||
    file.type === "image/png" ||
    file.type === "image/webp"
    ? file.type
    : null;
}

async function fileToDataUrl(file: File): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("Unable to read receipt image"));
    reader.onerror = () => reject(new Error("Unable to read receipt image"));
    reader.readAsDataURL(file);
  });
}

function formatReceiptFileSize(bytes: number): string {
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1_000))} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function Field({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`field ${className}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function StatusMark({ status }: { status: InvoiceStatus }) {
  return (
    <span className={`status status-${status}`}>
      <i aria-hidden="true" />
      {statusLabel(status)}
    </span>
  );
}

function ProfileLogo({
  displayName,
  url,
}: {
  displayName: string;
  url: string;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  return (
    <span className="profile-switch-logo">
      {url && !failed ? (
        <img
          src={url}
          alt={`${displayName} billing profile logo`}
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="profile-logo-fallback" aria-hidden="true">
          {profileInitials(displayName)}
        </span>
      )}
    </span>
  );
}

function PartyEditor({
  legend,
  party,
  onChange,
  action,
}: {
  legend: string;
  party: PartySnapshot;
  onChange: (party: PartySnapshot) => void;
  action?: ReactNode;
}) {
  const set = (key: keyof PartySnapshot, value: string) =>
    onChange({ ...party, [key]: value });
  return (
    <fieldset className="party-editor">
      <div className="subsection-head">
        <legend>{legend}</legend>
        {action}
      </div>
      <Field label="BUSINESS / NAME">
        <input
          value={party.name}
          onChange={(event) => set("name", event.target.value)}
          placeholder="Business or client name"
        />
      </Field>
      <div className="field-grid two">
        <Field label="CONTACT NAME">
          <input
            value={party.contactName}
            onChange={(event) => set("contactName", event.target.value)}
          />
        </Field>
        <Field label="EMAIL">
          <input
            type="email"
            value={party.email}
            onChange={(event) => set("email", event.target.value)}
          />
        </Field>
        <Field label="PHONE">
          <input
            value={party.phone}
            onChange={(event) => set("phone", event.target.value)}
          />
        </Field>
        <Field label="REGISTRATION NO.">
          <input
            value={party.registrationNumber}
            onChange={(event) =>
              set("registrationNumber", event.target.value)
            }
          />
        </Field>
      </div>
      <Field label="ADDRESS">
        <textarea
          rows={2}
          value={party.address}
          onChange={(event) => set("address", event.target.value)}
        />
      </Field>
    </fieldset>
  );
}

function Drawer({
  open,
  title,
  kicker,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  kicker: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);
  if (!open) return null;
  return (
    <div className="drawer-layer" role="presentation">
      <button
        className="drawer-backdrop"
        aria-label="Close panel"
        onClick={onClose}
      />
      <aside className="drawer" role="dialog" aria-modal="true">
        <header className="drawer-head">
          <div>
            <p className="eyebrow">{kicker}</p>
            <h2>{title}</h2>
          </div>
          <button
            ref={closeRef}
            className="close-button"
            type="button"
            aria-label="Close panel"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="drawer-body">{children}</div>
      </aside>
    </div>
  );
}

function Toast({ message }: { message: string }) {
  return message ? (
    <div className="toast" role="status">
      {message}
    </div>
  ) : null;
}

export default function App({ session, localMode = false }: WorkspaceProps) {
  const ownerUserId = session?.user.id ?? LOCAL_USER_ID;
  const signedInEmail = session?.user.email ?? "local-preview@invoice-index";
  const localSnapshot = useMemo(
    () => (localMode ? loadLocalWorkspace() : null),
    [localMode],
  );

  const [profiles, setProfiles] = useState<BillingProfile[]>(
    localSnapshot?.profiles ?? [],
  );
  const [clients, setClients] = useState<Client[]>(
    localSnapshot?.clients ?? [],
  );
  const [invoices, setInvoices] = useState<Invoice[]>(
    localSnapshot?.invoices ?? [],
  );
  const [activeProfileId, setActiveProfileId] = useState(
    localSnapshot?.activeProfileId ?? "",
  );
  const [invoice, setInvoice] = useState<Invoice | null>(() => {
    const current = localSnapshot?.invoices.find(
      (entry) => entry.id === localSnapshot.currentInvoiceId,
    );
    return current ?? null;
  });
  const [drawer, setDrawer] = useState<DrawerName>(null);
  const [saveState, setSaveState] = useState<SaveState>(
    localMode ? "LOCAL ONLY" : "SAVED",
  );
  const [lastSavedAt, setLastSavedAt] = useState("");
  const [loading, setLoading] = useState(!localMode);
  const [loadError, setLoadError] = useState("");
  const [toast, setToast] = useState("");
  const [zoom, setZoom] = useState<"fit" | "75" | "100" | "125">("fit");
  const [mobileView, setMobileView] = useState<"editor" | "preview">("editor");
  const [onboardingName, setOnboardingName] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const [recordSearch, setRecordSearch] = useState("");
  const [recordStatus, setRecordStatus] = useState<InvoiceStatus | "all">("all");
  const [clientSearch, setClientSearch] = useState("");
  const [clientDraft, setClientDraft] = useState<PartySnapshot | null>(null);
  const [customDiscount, setCustomDiscount] = useState("0.85");
  const [pngScale, setPngScale] = useState<1 | 2>(2);
  const [busyExport, setBusyExport] = useState(false);
  const [profileLogoUrls, setProfileLogoUrls] = useState<
    Record<string, string>
  >({});
  const [receiptImageUrls, setReceiptImageUrls] = useState<
    Record<string, string>
  >({});
  const [busyReceipts, setBusyReceipts] = useState(false);
  const [hiddenHistory, setHiddenHistory] = useState<Set<string>>(() => {
    if (!localMode) return new Set();
    try {
      const values: unknown = JSON.parse(
        localStorage.getItem("invoice-index.history-suppressions") ?? "[]",
      );
      return new Set(Array.isArray(values) ? values.filter((v) => typeof v === "string") : []);
    } catch {
      return new Set();
    }
  });
  const previewRef = useRef<HTMLDivElement>(null);
  const undoStack = useRef<Invoice[]>([]);
  const redoStack = useRef<Invoice[]>([]);
  const revisionApproved = useRef(false);
  const deletingProfileIds = useRef(new Set<string>());
  const invoiceRef = useRef(invoice);
  const invoicesRef = useRef(invoices);
  const profilesRef = useRef(profiles);
  const clientsRef = useRef(clients);
  const activeProfileIdRef = useRef(activeProfileId);
  const autosaveTimerRef = useRef<number | null>(null);
  const saveQueueRef = useRef<SerializedSaveQueue | null>(null);
  const latestSaveRequestRef = useRef(0);
  saveQueueRef.current ??= createSerializedSaveQueue();
  invoiceRef.current = invoice;
  invoicesRef.current = invoices;
  profilesRef.current = profiles;
  clientsRef.current = clients;
  activeProfileIdRef.current = activeProfileId;

  const activeProfile = profiles.find(
    (profile) => profile.id === activeProfileId,
  );
  const logoPathKey = profiles
    .map((profile) => `${profile.id}:${profile.logoPath}`)
    .join("|");
  const receiptPathKey = (invoice?.receipts ?? [])
    .map((receipt) => `${receipt.id}:${receipt.storagePath}`)
    .join("|");

  useEffect(() => {
    let active = true;

    const refreshUrls = async () => {
      const urls = await resolveProfileLogoUrls(profiles, async (path) => {
        if (!supabase) return null;
        const { data, error } = await supabase.storage
          .from("billing-logos")
          .createSignedUrl(path, 900);
        return error ? null : data.signedUrl;
      });
      if (active) setProfileLogoUrls(urls);
    };

    void refreshUrls();
    const refreshTimer = window.setInterval(
      () => void refreshUrls(),
      14 * 60 * 1000,
    );

    return () => {
      active = false;
      window.clearInterval(refreshTimer);
    };
  }, [logoPathKey]);

  useEffect(() => {
    let active = true;
    const receipts = invoice?.receipts ?? [];

    const refreshUrls = async () => {
      const urls = await resolveReceiptImageUrls(receipts, async (path) => {
        if (!supabase) return null;
        const { data, error } = await supabase.storage
          .from("invoice-receipts")
          .createSignedUrl(path, 900);
        return error ? null : data.signedUrl;
      });
      if (active) setReceiptImageUrls(urls);
    };

    void refreshUrls();
    const refreshTimer = window.setInterval(
      () => void refreshUrls(),
      14 * 60 * 1000,
    );

    return () => {
      active = false;
      window.clearInterval(refreshTimer);
    };
  }, [receiptPathKey]);

  const logoUrl = activeProfile
    ? (profileLogoUrls[activeProfile.id] ?? "")
    : "";

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 3000);
  }, []);

  const persistInvoiceSnapshot = useCallback(
    (snapshot: Invoice, source: SaveSource): Promise<void> => {
      const normalized = normalizeInvoice(snapshot);
      const requestId = ++latestSaveRequestRef.current;
      setSaveState("SAVING…");

      return saveQueueRef.current!.enqueue(async () => {
        if (deletingProfileIds.current.has(normalized.billingProfileId)) return;

        try {
          if (!localMode && supabase) {
            await saveCloudInvoice(supabase, normalized);
          }

          const nextInvoices = mergeById(invoicesRef.current, normalized);
          invoicesRef.current = nextInvoices;
          setInvoices(nextInvoices);

          if (localMode) {
            saveLocalWorkspace({
              version: 1,
              profiles: profilesRef.current,
              clients: clientsRef.current,
              invoices: nextInvoices,
              activeProfileId: activeProfileIdRef.current,
              currentInvoiceId: invoiceRef.current?.id ?? normalized.id,
            });
          }

          const savedAt = formatSavedTime();
          const isLatestSnapshot =
            requestId === latestSaveRequestRef.current &&
            invoiceRef.current === snapshot;
          if (isLatestSnapshot) {
            setLastSavedAt(savedAt);
            setSaveState("SAVED");
          }
          if (source === "manual") {
            showToast(
              isLatestSnapshot
                ? `INVOICE SAVED · ${savedAt}`
                : "INVOICE VERSION SAVED · NEWER CHANGES PENDING",
            );
          }
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Unable to save invoice";
          const isLatestSnapshot =
            requestId === latestSaveRequestRef.current &&
            invoiceRef.current === snapshot;
          if (isLatestSnapshot) {
            setSaveState("SYNC ERROR");
            setLoadError(message);
          }
          if (source === "manual") showToast(`SAVE FAILED · ${message}`);
          throw error;
        }
      });
    },
    [localMode, showToast],
  );

  const buildBlankForProfile = useCallback(
    (
      profile: BillingProfile,
      allInvoices: Invoice[],
      overrideNumber?: string,
    ) => {
      const now = new Date();
      const numbers = allInvoices
        .filter((entry) => entry.billingProfileId === profile.id)
        .map((entry) => entry.number);
      return createBlankInvoice({
        ownerUserId,
        profileId: profile.id,
        profileName: profile.displayName,
        invoiceNumber:
          overrideNumber ??
          nextInvoiceNumber(profile.invoicePrefix, now.getFullYear(), numbers),
        paymentDays: profile.defaultPaymentDays,
        issuer: profileIssuer(profile),
        paymentInstructions: profile.paymentInstructions,
      });
    },
    [ownerUserId],
  );

  useEffect(() => {
    if (localMode || !supabase) return;
    let active = true;
    void loadCloudWorkspace(supabase)
      .then((data) => {
        if (!active) return;
        setProfiles(data.profiles);
        setClients(data.clients);
        setInvoices(data.invoices);
        setHiddenHistory(new Set(data.hiddenHistory));
        const firstProfile =
          data.profiles.find((profile) => !profile.archivedAt) ??
          data.profiles[0];
        if (firstProfile) {
          setActiveProfileId(firstProfile.id);
          const latest = data.invoices.find(
            (entry) => entry.billingProfileId === firstProfile.id,
          );
          setInvoice(
            latest ?? buildBlankForProfile(firstProfile, data.invoices),
          );
        }
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setLoadError(
          error instanceof Error
            ? error.message
            : "Unable to load private workspace",
        );
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [buildBlankForProfile, localMode]);

  useEffect(() => {
    if (
      !localMode ||
      invoice ||
      !activeProfile ||
      profiles.length === 0
    ) {
      return;
    }
    setInvoice(buildBlankForProfile(activeProfile, invoices));
  }, [activeProfile, buildBlankForProfile, invoice, invoices, localMode, profiles.length]);

  const updateInvoice = useCallback(
    (
      updater: (current: Invoice) => Invoice,
      options: { monetary?: boolean; history?: boolean } = {},
    ) => {
      if (!invoice) return;
      if (
        options.monetary &&
        (invoice.payments.length > 0 ||
          invoice.status === "paid" ||
          invoice.status === "partially_paid")
      ) {
        showToast("AMOUNTS LOCKED · DUPLICATE TO REVISE");
        return;
      }
      if (
        options.monetary &&
        invoice.status === "sent" &&
        !revisionApproved.current
      ) {
        const approved = window.confirm(
          "This invoice was sent. Continue and save a revision snapshot?",
        );
        if (!approved) return;
        revisionApproved.current = true;
      }
      if (options.history !== false) {
        undoStack.current.push(structuredClone(invoice));
        if (undoStack.current.length > 80) undoStack.current.shift();
        redoStack.current = [];
      }
      setInvoice(
        normalizeInvoice({
          ...updater(invoice),
          updatedAt: new Date().toISOString(),
        }),
      );
    },
    [invoice, showToast],
  );

  useEffect(() => {
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (!invoice || !activeProfile) return;
    if (!isMeaningfulInvoice(invoice)) {
      setSaveState(localMode ? "LOCAL ONLY" : "SAVED");
      return;
    }

    setSaveState("UNSAVED");
    const timer = window.setTimeout(() => {
      if (autosaveTimerRef.current === timer) {
        autosaveTimerRef.current = null;
      }
      void persistInvoiceSnapshot(invoice, "auto").catch(() => undefined);
    }, 950);
    autosaveTimerRef.current = timer;

    return () => {
      if (autosaveTimerRef.current === timer) {
        window.clearTimeout(timer);
        autosaveTimerRef.current = null;
      }
    };
  }, [activeProfile, invoice, localMode, persistInvoiceSnapshot]);

  const saveInvoiceManually = useCallback(() => {
    const latestInvoice = invoiceRef.current;
    if (!latestInvoice || !isMeaningfulInvoice(latestInvoice)) {
      showToast("ADD INVOICE DETAILS BEFORE SAVING");
      return;
    }
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    void persistInvoiceSnapshot(latestInvoice, "manual").catch(() => undefined);
  }, [persistInvoiceSnapshot, showToast]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "s"
      ) {
        event.preventDefault();
        saveInvoiceManually();
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [saveInvoiceManually]);

  function undo() {
    if (!invoice) return;
    const previous = undoStack.current.pop();
    if (!previous) return;
    redoStack.current.push(structuredClone(invoice));
    setInvoice(previous);
  }

  function redo() {
    if (!invoice) return;
    const next = redoStack.current.pop();
    if (!next) return;
    undoStack.current.push(structuredClone(invoice));
    setInvoice(next);
  }

  async function createFirstProfile() {
    const displayName = onboardingName.trim();
    if (!displayName) return;
    const profile = createProfile(ownerUserId, displayName);
    setProfiles([profile]);
    setActiveProfileId(profile.id);
    setInvoice(buildBlankForProfile(profile, []));
    if (!localMode && supabase) await saveCloudProfile(supabase, profile);
    if (localMode) {
      saveLocalWorkspace({
        version: 1,
        profiles: [profile],
        clients: [],
        invoices: [],
        activeProfileId: profile.id,
        currentInvoiceId: "",
      });
    }
    showToast("BILLING PROFILE CREATED");
  }

  function newInvoice() {
    if (!activeProfile) return;
    if (activeProfile.archivedAt) {
      showToast("ARCHIVED PROFILE · RESTORE IT TO CREATE INVOICES");
      return;
    }
    if (
      invoice &&
      isMeaningfulInvoice(invoice) &&
      (saveState === "UNSAVED" || saveState === "SAVING…") &&
      !window.confirm(
        "Current changes have not finished saving. Start a new invoice?",
      )
    ) {
      return;
    }
    const blank = buildBlankForProfile(activeProfile, invoices);
    undoStack.current = [];
    redoStack.current = [];
    revisionApproved.current = false;
    setInvoice(blank);
    setDrawer(null);
    setMobileView("editor");
    window.setTimeout(
      () => document.getElementById("client-name")?.focus(),
      0,
    );
  }

  function duplicateInvoice() {
    if (!invoice || !activeProfile) return;
    const number = nextInvoiceNumber(
      activeProfile.invoicePrefix,
      new Date().getFullYear(),
      invoices.map((entry) => entry.number),
    );
    const now = new Date().toISOString();
    const duplicate = normalizeInvoice({
      ...structuredClone(invoice),
      id: createId(),
      number,
      status: "draft",
      payments: [],
      receipts: [],
      paidMinor: 0,
      balanceMinor: invoice.totalMinor,
      items: invoice.items.map((item) => ({ ...item, id: createId() })),
      createdAt: now,
      updatedAt: now,
    });
    setInvoice(duplicate);
    revisionApproved.current = false;
    showToast("DUPLICATED AS NEW DRAFT");
  }

  function switchProfile(profile: BillingProfile) {
    setActiveProfileId(profile.id);
    const existing = invoices.find(
      (entry) => entry.billingProfileId === profile.id,
    );
    setInvoice(existing ?? buildBlankForProfile(profile, invoices));
    setDrawer(null);
    revisionApproved.current = false;
  }

  async function persistProfile(profile: BillingProfile) {
    const next = { ...profile, updatedAt: new Date().toISOString() };
    setProfiles((values) => mergeById(values, next));
    if (!localMode && supabase) await saveCloudProfile(supabase, next);
    showToast("BILLING PROFILE SAVED");
  }

  async function createAdditionalProfile() {
    const displayName = newProfileName.trim();
    if (!displayName) return;
    const profile = createProfile(ownerUserId, displayName);
    setProfiles((values) => [...values, profile]);
    if (!localMode && supabase) await saveCloudProfile(supabase, profile);
    setNewProfileName("");
    switchProfile(profile);
    showToast("NEW BILLING PROFILE CREATED");
  }

  async function removeProfile(profile: BillingProfile) {
    const profileInvoices = invoices.filter(
      (entry) => entry.billingProfileId === profile.id,
    );
    const profileClients = clients.filter(
      (entry) => entry.billingProfileId === profile.id,
    );
    const approved = window.confirm(
      [
        `Permanently delete “${profile.displayName}”?`,
        "",
        `This will delete ${profileInvoices.length} invoice${profileInvoices.length === 1 ? "" : "s"}, ${profileClients.length} client${profileClients.length === 1 ? "" : "s"}, all related payments, receipt images, saved text history, and its logo.`,
        "",
        "This cannot be undone.",
      ].join("\n"),
    );
    if (!approved) return;

    deletingProfileIds.current.add(profile.id);
    setSaveState(localMode ? "LOCAL ONLY" : "SAVING…");

    try {
      if (!localMode && supabase) {
        await deleteCloudProfile(supabase, profile);
      }

      const nextProfiles = profiles.filter((entry) => entry.id !== profile.id);
      const nextClients = clients.filter(
        (entry) => entry.billingProfileId !== profile.id,
      );
      const nextInvoices = invoices.filter(
        (entry) => entry.billingProfileId !== profile.id,
      );
      const nextHiddenHistory = new Set(
        [...hiddenHistory].filter((key) => !key.startsWith(`${profile.id}:`)),
      );
      const nextActiveProfile =
        nextProfiles.find((entry) => !entry.archivedAt) ?? nextProfiles[0];
      const nextInvoice = nextActiveProfile
        ? (nextInvoices.find(
            (entry) => entry.billingProfileId === nextActiveProfile.id,
          ) ?? buildBlankForProfile(nextActiveProfile, nextInvoices))
        : null;

      setProfiles(nextProfiles);
      setClients(nextClients);
      setInvoices(nextInvoices);
      setHiddenHistory(nextHiddenHistory);
      setActiveProfileId(nextActiveProfile?.id ?? "");
      setInvoice(nextInvoice);
      setProfileLogoUrls((urls) => {
        const next = { ...urls };
        delete next[profile.id];
        return next;
      });
      setDrawer(null);
      undoStack.current = [];
      redoStack.current = [];
      revisionApproved.current = false;

      if (localMode) {
        localStorage.setItem(
          "invoice-index.history-suppressions",
          JSON.stringify([...nextHiddenHistory]),
        );
        saveLocalWorkspace({
          version: 1,
          profiles: nextProfiles,
          clients: nextClients,
          invoices: nextInvoices,
          activeProfileId: nextActiveProfile?.id ?? "",
          currentInvoiceId: nextInvoice?.id ?? "",
        });
      }

      setSaveState(localMode ? "LOCAL ONLY" : "SAVED");
      showToast("BILLING PROFILE PERMANENTLY DELETED");
    } catch (error) {
      setSaveState("SYNC ERROR");
      setLoadError(
        error instanceof Error ? error.message : "Unable to delete billing profile",
      );
      showToast("PROFILE DELETE FAILED");
    } finally {
      deletingProfileIds.current.delete(profile.id);
    }
  }

  async function handleLogo(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !activeProfile) return;
    if (file.size > 2_000_000) {
      showToast("LOGO MUST BE UNDER 2 MB");
      return;
    }
    if (localMode) {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          void persistProfile({ ...activeProfile, logoPath: reader.result });
        }
      };
      reader.readAsDataURL(file);
      return;
    }
    if (!supabase) return;
    const extension = file.name.split(".").pop()?.toLowerCase() || "png";
    const path = `${ownerUserId}/${activeProfile.id}/logo.${extension}`;
    const { error } = await supabase.storage
      .from("billing-logos")
      .upload(path, file, { upsert: true });
    if (error) {
      showToast(error.message);
      return;
    }
    await persistProfile({ ...activeProfile, logoPath: path });
  }

  async function handleReceipts(event: ChangeEvent<HTMLInputElement>) {
    const selectedFiles = [...(event.target.files ?? [])];
    event.target.value = "";
    if (!invoice || selectedFiles.length === 0) return;

    const remainingSlots =
      MAX_RECEIPTS_PER_INVOICE - invoice.receipts.length;
    if (remainingSlots <= 0) {
      showToast(`MAXIMUM ${MAX_RECEIPTS_PER_INVOICE} RECEIPTS PER INVOICE`);
      return;
    }

    const files = selectedFiles.slice(0, remainingSlots);
    const invalid = files.find(
      (file) =>
        !supportedReceiptMimeType(file) ||
        file.size <= 0 ||
        file.size > MAX_RECEIPT_BYTES,
    );
    if (invalid) {
      showToast("RECEIPTS MUST BE JPG, PNG OR WEBP · MAX 10 MB EACH");
      return;
    }

    setBusyReceipts(true);
    setSaveState(localMode ? "LOCAL ONLY" : "SAVING…");
    let working = normalizeInvoice(invoice);
    let nextPosition =
      Math.max(-1, ...working.receipts.map((receipt) => receipt.position)) + 1;

    try {
      if (!localMode && supabase) {
        await saveCloudInvoice(supabase, working);
      }

      for (const file of files) {
        const mimeType = supportedReceiptMimeType(file);
        if (!mimeType) continue;
        const receiptId = createId();
        const now = new Date().toISOString();
        const receipt: ReceiptAttachment =
          !localMode && supabase
            ? await uploadCloudReceipt(supabase, {
                invoice: working,
                receiptId,
                file,
                mimeType,
                position: nextPosition,
              })
            : {
                id: receiptId,
                ownerUserId,
                billingProfileId: working.billingProfileId,
                invoiceId: working.id,
                storagePath: await fileToDataUrl(file),
                fileName: file.name.slice(0, 255),
                mimeType,
                fileSize: file.size,
                position: nextPosition,
                createdAt: now,
              };
        nextPosition += 1;
        working = normalizeInvoice({
          ...working,
          receipts: [...working.receipts, receipt],
          updatedAt: now,
        });
        setInvoice(working);
        setInvoices((values) => mergeById(values, working));
      }

      if (localMode) {
        const nextInvoices = mergeById(invoices, working);
        saveLocalWorkspace({
          version: 1,
          profiles,
          clients,
          invoices: nextInvoices,
          activeProfileId,
          currentInvoiceId: working.id,
        });
      }
      setSaveState(localMode ? "LOCAL ONLY" : "SAVED");
      showToast(
        `${files.length} RECEIPT${files.length === 1 ? "" : "S"} ATTACHED`,
      );
    } catch (error) {
      setSaveState("SYNC ERROR");
      setLoadError(
        error instanceof Error ? error.message : "Unable to upload receipt",
      );
      showToast("RECEIPT UPLOAD FAILED");
    } finally {
      setBusyReceipts(false);
    }
  }

  async function removeReceipt(receipt: ReceiptAttachment) {
    if (!invoice) return;
    if (!window.confirm(`Permanently delete receipt “${receipt.fileName}”?`)) {
      return;
    }

    try {
      if (!localMode && supabase) {
        await deleteCloudReceipt(supabase, receipt);
      }
      const next = normalizeInvoice({
        ...invoice,
        receipts: invoice.receipts.filter((entry) => entry.id !== receipt.id),
        updatedAt: new Date().toISOString(),
      });
      setInvoice(next);
      setInvoices((values) => mergeById(values, next));
      setReceiptImageUrls((urls) => {
        const nextUrls = { ...urls };
        delete nextUrls[receipt.id];
        return nextUrls;
      });

      if (localMode) {
        const nextInvoices = mergeById(invoices, next);
        saveLocalWorkspace({
          version: 1,
          profiles,
          clients,
          invoices: nextInvoices,
          activeProfileId,
          currentInvoiceId: next.id,
        });
      }
      showToast("RECEIPT PERMANENTLY DELETED");
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "Unable to delete receipt",
      );
      showToast("RECEIPT DELETE FAILED");
    }
  }

  function openClientPicker() {
    setClientDraft(invoice?.client ?? null);
    setDrawer("clients");
  }

  async function saveClientRecord() {
    if (!invoice || !clientDraft?.name.trim()) return;
    const now = new Date().toISOString();
    const existing = clients.find(
      (entry) =>
        entry.billingProfileId === invoice.billingProfileId &&
        entry.party.name.toLowerCase() === clientDraft.name.toLowerCase(),
    );
    const record: Client = {
      id: existing?.id ?? createId(),
      ownerUserId,
      billingProfileId: invoice.billingProfileId,
      party: clientDraft,
      lastUsedAt: now,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    setClients((values) => mergeById(values, record));
    updateInvoice(
      (current) => ({ ...current, client: clientDraft }),
      { history: true },
    );
    if (!localMode && supabase) await saveCloudClient(supabase, record);
    setDrawer(null);
    showToast(existing ? "CLIENT UPDATED" : "CLIENT SAVED");
  }

  async function removeClient(record: Client) {
    if (!window.confirm(`Delete client “${record.party.name}”?`)) return;
    setClients((values) => values.filter((entry) => entry.id !== record.id));
    if (!localMode && supabase) await deleteCloudClient(supabase, record.id);
    showToast("CLIENT DELETED");
  }

  async function removeInvoice(record: Invoice) {
    if (
      !window.confirm(
        `Permanently delete ${record.number}? Its receipt images and unique autocomplete history will also be removed.`,
      )
    ) {
      return;
    }
    const next = invoices.filter((entry) => entry.id !== record.id);
    setInvoices(next);
    if (!localMode && supabase) await deleteCloudInvoice(supabase, record);
    if (invoice?.id === record.id && activeProfile) {
      setInvoice(buildBlankForProfile(activeProfile, next));
    }
    showToast("INVOICE DELETED");
  }

  const suggestions = useMemo(() => {
    if (!activeProfile) {
      return { descriptions: [], clientNotes: [], internalNotes: [] };
    }
    const relevant = invoices.filter(
      (entry) => entry.billingProfileId === activeProfile.id,
    );
    const unique = (
      kind: "description" | "client_notes" | "internal_notes",
      values: string[],
    ) =>
      [
        ...new Set(
          values
            .map((value) => value.trim())
            .filter(Boolean)
            .filter(
              (value) =>
                !hiddenHistory.has(
                  `${activeProfile.id}:${kind}:${value.toLocaleLowerCase("en")}`,
                ),
            ),
        ),
      ];
    return {
      descriptions: unique(
        "description",
        relevant.flatMap((entry) =>
          entry.items.map((item) => item.description),
        ),
      ),
      clientNotes: unique(
        "client_notes",
        relevant.map((entry) => entry.clientNotes),
      ),
      internalNotes: unique(
        "internal_notes",
        relevant.map((entry) => entry.internalNotes),
      ),
    };
  }, [activeProfile, hiddenHistory, invoices]);

  async function hideHistory(
    kind: "description" | "client_notes" | "internal_notes",
    value: string,
  ) {
    if (!activeProfile) return;
    const key = `${activeProfile.id}:${kind}:${value.toLocaleLowerCase("en")}`;
    const next = new Set(hiddenHistory);
    next.add(key);
    setHiddenHistory(next);
    if (localMode) {
      localStorage.setItem(
        "invoice-index.history-suppressions",
        JSON.stringify([...next]),
      );
    } else if (supabase) {
      await suppressCloudHistory(supabase, {
        ownerUserId,
        billingProfileId: activeProfile.id,
        kind,
        value,
      });
    }
    showToast("HISTORY ITEM REMOVED");
  }

  const filteredRecords = useMemo(() => {
    const search = recordSearch.trim().toLowerCase();
    return invoices
      .filter((entry) => entry.billingProfileId === activeProfileId)
      .filter((entry) => recordStatus === "all" || entry.status === recordStatus)
      .filter(
        (entry) =>
          !search ||
          entry.number.toLowerCase().includes(search) ||
          entry.client.name.toLowerCase().includes(search),
      )
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [activeProfileId, invoices, recordSearch, recordStatus]);

  const filteredClients = useMemo(() => {
    const search = clientSearch.trim().toLowerCase();
    return clients
      .filter((entry) => entry.billingProfileId === activeProfileId)
      .filter(
        (entry) =>
          !search ||
          entry.party.name.toLowerCase().includes(search) ||
          entry.party.contactName.toLowerCase().includes(search) ||
          entry.party.email.toLowerCase().includes(search),
      );
  }, [activeProfileId, clientSearch, clients]);

  if (loading) {
    return (
      <main className="loading-screen">
        <p className="eyebrow">INVOICE INDEX / PRIVATE WORKSPACE</p>
        <h1>SYNCING RECORDS…</h1>
      </main>
    );
  }

  if (profiles.length === 0) {
    return (
      <main className="onboarding-screen">
        <section className="onboarding-panel">
          {localMode && (
            <div className="setup-banner">
              LOCAL SETUP MODE · SUPABASE ENVIRONMENT VARIABLES ARE NOT SET
            </div>
          )}
          <p className="eyebrow">01 / FIRST BILLING PROFILE</p>
          <h1>HOW SHOULD YOUR INVOICES READ?</h1>
          <p>
            只需填寫顯示名稱即可開始。Logo、地址、商業登記、付款資料及品牌顏色可稍後加入。
          </p>
          <Field label="DISPLAY NAME / 顯示名稱">
            <input
              autoFocus
              value={onboardingName}
              onChange={(event) => setOnboardingName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void createFirstProfile();
              }}
              placeholder="Your name, studio or business"
            />
          </Field>
          <button
            className="button primary"
            type="button"
            disabled={!onboardingName.trim()}
            onClick={() => void createFirstProfile()}
          >
            CREATE PROFILE + START
          </button>
          <p className="small-note">
            {localMode
              ? "Local setup mode stores data only in this browser. Add the Supabase environment values to enable private cross-device sync."
              : `Signed in as ${signedInEmail}`}
          </p>
        </section>
      </main>
    );
  }

  if (!activeProfile || !invoice) {
    return (
      <main className="loading-screen">
        <h1>SELECT A BILLING PROFILE</h1>
        {profiles.map((profile) => (
          <button
            className="button secondary"
            type="button"
            key={profile.id}
            onClick={() => switchProfile(profile)}
          >
            {profile.displayName}
          </button>
        ))}
      </main>
    );
  }

  const overpaidMinor = Math.max(0, invoice.paidMinor - invoice.totalMinor);
  const amountsLocked =
    invoice.payments.length > 0 ||
    invoice.status === "paid" ||
    invoice.status === "partially_paid";
  const firstInvoicePageCapacity =
    invoice.purchaseOrderNumber.trim() || invoice.projectReference.trim()
      ? 10
      : 12;
  const invoicePageCount =
    invoice.items.length <= firstInvoicePageCapacity
      ? 1
      : 1 +
        Math.ceil(
          (invoice.items.length - firstInvoicePageCapacity) / 18,
        );

  return (
    <div className="app-shell">
      {localMode && (
        <div className="setup-strip">
          LOCAL SETUP MODE · DATA STAYS IN THIS BROWSER · ADD SUPABASE ENV TO
          ENABLE AUTH + SYNC
        </div>
      )}
      <header className="masthead">
        <button
          type="button"
          className="brand-lockup"
          onClick={() => setDrawer("profiles")}
          aria-label="Open billing profiles"
        >
          <span className="brand-mark" aria-hidden="true">
            <i />
            <i />
          </span>
          <span>
            <strong>INVOICE INDEX</strong>
            <small>BILLING SYSTEM / 發票工作台</small>
          </span>
        </button>
        <div className="save-indicator">
          <StatusMark status={invoice.status} />
          <span className="save-state-copy" aria-live="polite">
            <strong>
              {saveState}
              {saveState === "SAVED" && lastSavedAt ? ` ${lastSavedAt}` : ""}
            </strong>
            <small>AUTO-SAVE ON</small>
          </span>
        </div>
        <div className="header-actions">
          <button type="button" onClick={() => setDrawer("records")}>
            RECORDS <span>記錄</span>
          </button>
          <button type="button" onClick={() => setDrawer("export")}>
            EXPORT <span>輸出</span>
          </button>
        </div>
      </header>

      <nav className="command-bar" aria-label="Invoice actions">
        <div className="command-group">
          <button className="new-button" type="button" onClick={newInvoice}>
            <b>＋</b> NEW INVOICE
          </button>
          <button type="button" onClick={duplicateInvoice}>
            DUPLICATE
          </button>
          <button
            type="button"
            disabled={undoStack.current.length === 0}
            onClick={undo}
          >
            UNDO
          </button>
          <button
            type="button"
            disabled={redoStack.current.length === 0}
            onClick={redo}
          >
            REDO
          </button>
          <button
            className="manual-save-button"
            type="button"
            disabled={saveState === "SAVING…"}
            onClick={saveInvoiceManually}
          >
            <strong>
              {saveState === "SAVING…" ? "SAVING…" : "SAVE INVOICE"}
            </strong>
            <span>儲存發票 · AUTO-SAVE ON</span>
          </button>
        </div>
        <div className="mobile-view-switch">
          <button
            className={mobileView === "editor" ? "active" : ""}
            type="button"
            onClick={() => setMobileView("editor")}
          >
            EDITOR
          </button>
          <button
            className={mobileView === "preview" ? "active" : ""}
            type="button"
            onClick={() => setMobileView("preview")}
          >
            PREVIEW
          </button>
        </div>
        <div className="document-meta">
          <span>{invoice.number}</span>
          <select
            aria-label="Paper size"
            value={invoice.paperSize}
            onChange={(event) =>
              updateInvoice(
                (current) => ({
                  ...current,
                  paperSize: event.target.value as Invoice["paperSize"],
                }),
                { history: false },
              )
            }
          >
            <option value="a4">A4</option>
            <option value="letter">LETTER</option>
          </select>
          <select
            aria-label="Preview zoom"
            value={zoom}
            onChange={(event) =>
              setZoom(event.target.value as typeof zoom)
            }
          >
            <option value="fit">FIT</option>
            <option value="75">75%</option>
            <option value="100">100%</option>
            <option value="125">125%</option>
          </select>
          <button type="button" onClick={() => setDrawer("account")}>
            ACCOUNT
          </button>
        </div>
      </nav>

      {loadError && (
        <div className="error-banner" role="alert">
          <span>SYNC NOTICE · {loadError}</span>
          <button type="button" onClick={() => setLoadError("")}>
            DISMISS
          </button>
        </div>
      )}

      <main className="workspace">
        <div
          className={`editor-pane ${mobileView !== "editor" ? "mobile-hidden" : ""}`}
        >
          <section className="editor-section">
            <div className="section-heading">
              <span>01</span>
              <h2>FROM + BILL TO</h2>
            </div>
            <div className="party-pair">
              <PartyEditor
                legend="FROM / 開票者"
                party={invoice.issuer}
                onChange={(party) =>
                  updateInvoice((current) => ({ ...current, issuer: party }))
                }
                action={
                  <button
                    className="tertiary"
                    type="button"
                    onClick={() => setDrawer("profiles")}
                  >
                    SELECT PROFILE
                  </button>
                }
              />
              <div id="client-name">
                <PartyEditor
                  legend="BILL TO / 客戶"
                  party={invoice.client}
                  onChange={(party) =>
                    updateInvoice((current) => ({ ...current, client: party }))
                  }
                  action={
                    <button
                      className="tertiary"
                      type="button"
                      onClick={openClientPicker}
                    >
                      SELECT / SAVE CLIENT
                    </button>
                  }
                />
              </div>
            </div>
          </section>

          <section className="editor-section">
            <div className="section-heading">
              <span>02</span>
              <h2>INVOICE DETAILS</h2>
            </div>
            <div className="field-grid four">
              <Field label="INVOICE NUMBER">
                <input
                  value={invoice.number}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      number: event.target.value.toUpperCase(),
                    }))
                  }
                  aria-invalid={invoices.some(
                    (entry) =>
                      entry.id !== invoice.id &&
                      entry.billingProfileId === invoice.billingProfileId &&
                      entry.number.toLowerCase() === invoice.number.toLowerCase(),
                  )}
                />
              </Field>
              <Field label="ISSUE DATE">
                <input
                  type="date"
                  value={invoice.issueDate}
                  onChange={(event) => {
                    const issueDate = event.target.value;
                    updateInvoice((current) => ({
                      ...current,
                      issueDate,
                      dueDate: addDays(
                        issueDate,
                        activeProfile.defaultPaymentDays,
                      ),
                    }));
                  }}
                />
              </Field>
              <Field label="DUE DATE">
                <input
                  type="date"
                  value={invoice.dueDate}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      dueDate: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="CURRENCY">
                <input value="HKD" disabled />
              </Field>
              <Field label="PO NUMBER">
                <input
                  value={invoice.purchaseOrderNumber}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      purchaseOrderNumber: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="PROJECT / JOB REF.">
                <input
                  value={invoice.projectReference}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      projectReference: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="LANGUAGE">
                <select
                  value={invoice.language}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      language: event.target.value as Invoice["language"],
                    }))
                  }
                >
                  <option value="zh-HK">繁體中文</option>
                  <option value="en">ENGLISH</option>
                </select>
              </Field>
              <Field label="STATUS">
                <select
                  value={invoice.status}
                  onChange={(event) =>
                    updateInvoice(
                      (current) => ({
                        ...current,
                        status: event.target.value as InvoiceStatus,
                      }),
                      { history: false },
                    )
                  }
                >
                  <option value="draft">DRAFT</option>
                  <option value="sent">SENT</option>
                  <option value="void">VOID</option>
                  <option value="overdue">OVERDUE</option>
                  <option value="partially_paid" disabled>
                    PARTIALLY PAID · AUTO
                  </option>
                  <option value="paid" disabled>
                    PAID · AUTO
                  </option>
                </select>
              </Field>
            </div>
          </section>

          <section className="editor-section line-items-section">
            <div className="section-heading">
              <span>03</span>
              <h2>LINE ITEMS</h2>
              {amountsLocked && (
                <em>LOCKED AFTER PAYMENT · DUPLICATE TO REVISE</em>
              )}
            </div>
            <div className="line-table-wrap">
              <table className="line-table">
                <thead>
                  <tr>
                    <th>DESCRIPTION</th>
                    <th>QTY</th>
                    <th>RATE</th>
                    <th>AMOUNT</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={item.id}>
                      <td data-label="DESCRIPTION">
                        <textarea
                          rows={1}
                          value={item.description}
                          disabled={amountsLocked}
                          onChange={(event) =>
                            updateInvoice(
                              (current) => ({
                                ...current,
                                items: current.items.map((entry) =>
                                  entry.id === item.id
                                    ? {
                                        ...entry,
                                        description: event.target.value,
                                      }
                                    : entry,
                                ),
                              }),
                              { monetary: true },
                            )
                          }
                        />
                        {suggestions.descriptions.length > 0 && (
                          <select
                            className="history-apply"
                            aria-label={`Use saved description for line ${index + 1}`}
                            value=""
                            onChange={(event) => {
                              if (!event.target.value) return;
                              updateInvoice(
                                (current) => ({
                                  ...current,
                                  items: current.items.map((entry) =>
                                    entry.id === item.id
                                      ? {
                                          ...entry,
                                          description: event.target.value,
                                        }
                                      : entry,
                                  ),
                                }),
                                { monetary: true },
                              );
                            }}
                          >
                            <option value="">HISTORY…</option>
                            {suggestions.descriptions.map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td data-label="QTY">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.001"
                          value={item.quantity}
                          disabled={amountsLocked}
                          onChange={(event) =>
                            updateInvoice(
                              (current) => ({
                                ...current,
                                items: current.items.map((entry) =>
                                  entry.id === item.id
                                    ? {
                                        ...entry,
                                        quantity:
                                          Number(event.target.value) || 0,
                                      }
                                    : entry,
                                ),
                              }),
                              { monetary: true },
                            )
                          }
                        />
                      </td>
                      <td data-label="RATE">
                        <MoneyInput
                          valueMinor={item.rateMinor}
                          disabled={amountsLocked}
                          ariaLabel={`Rate for line ${index + 1}`}
                          onMinorChange={(rateMinor) =>
                            updateInvoice(
                              (current) => ({
                                ...current,
                                items: current.items.map((entry) =>
                                  entry.id === item.id
                                    ? {
                                        ...entry,
                                        rateMinor,
                                      }
                                    : entry,
                                ),
                              }),
                              { monetary: true },
                            )
                          }
                        />
                      </td>
                      <td data-label="AMOUNT">
                        <output>{formatAmount(item.amountMinor)}</output>
                      </td>
                      <td>
                        <button
                          className="row-remove"
                          type="button"
                          aria-label={`Remove line ${index + 1}`}
                          disabled={amountsLocked}
                          onClick={() => {
                            const populated =
                              item.description ||
                              item.rateMinor ||
                              item.unit;
                            updateInvoice(
                              (current) => ({
                                ...current,
                                items:
                                  current.items.length === 1
                                    ? [createLineItem()]
                                    : current.items.filter(
                                        (entry) => entry.id !== item.id,
                                      ),
                              }),
                              { monetary: true },
                            );
                            if (populated) showToast("LINE REMOVED · UNDO AVAILABLE");
                          }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <datalist id="description-history">
              {suggestions.descriptions.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
            <button
              className="add-line"
              type="button"
              disabled={amountsLocked}
              onClick={() =>
                updateInvoice(
                  (current) => ({
                    ...current,
                    items: [...current.items, createLineItem()],
                  }),
                  { monetary: true },
                )
              }
            >
              ＋ ADD LINE
            </button>

            <div className="discount-and-totals">
              <div className="discount-controls">
                <Field label="DISCOUNT / 折扣">
                  <select
                    disabled={amountsLocked}
                    value={
                      !invoice.discount
                        ? "none"
                        : invoice.discount.mode === "fixed"
                          ? "fixed"
                          : [0.9, 0.8].includes(invoice.discount.multiplier)
                            ? String(invoice.discount.multiplier)
                            : "custom"
                    }
                    onChange={(event) => {
                      const value = event.target.value;
                      updateInvoice(
                        (current) => ({
                          ...current,
                          discount:
                            value === "none"
                              ? null
                              : value === "fixed"
                                ? { mode: "fixed", amountMinor: 0 }
                                : value === "custom"
                                  ? {
                                      mode: "multiplier",
                                      multiplier:
                                        Number(customDiscount) || 0.85,
                                    }
                                  : {
                                      mode: "multiplier",
                                      multiplier: Number(value),
                                    },
                        }),
                        { monetary: true },
                      );
                    }}
                  >
                    <option value="none">NO DISCOUNT</option>
                    <option value="0.9">9折 · 10% OFF</option>
                    <option value="0.8">8折 · 20% OFF</option>
                    <option value="custom">CUSTOM MULTIPLIER</option>
                    <option value="fixed">FIXED HKD</option>
                  </select>
                </Field>
                {invoice.discount?.mode === "multiplier" &&
                  ![0.9, 0.8].includes(invoice.discount.multiplier) && (
                    <Field label="MULTIPLIER · 0–1">
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={customDiscount}
                        disabled={amountsLocked}
                        onChange={(event) => {
                          setCustomDiscount(event.target.value);
                          updateInvoice(
                            (current) => ({
                              ...current,
                              discount: {
                                mode: "multiplier",
                                multiplier: Number(event.target.value),
                              },
                            }),
                            { monetary: true, history: false },
                          );
                        }}
                      />
                    </Field>
                  )}
                {invoice.discount?.mode === "fixed" && (
                  <Field label="FIXED DISCOUNT HKD">
                    <input
                      inputMode="decimal"
                      value={(invoice.discount.amountMinor / 100).toFixed(2)}
                      disabled={amountsLocked}
                      onChange={(event) =>
                        updateInvoice(
                          (current) => ({
                            ...current,
                            discount: {
                              mode: "fixed",
                              amountMinor: majorToMinor(event.target.value),
                            },
                          }),
                          { monetary: true },
                        )
                      }
                    />
                  </Field>
                )}
              </div>
              <div className="totals-block">
                <div>
                  <span>SUBTOTAL</span>
                  <strong>{formatHKD(invoice.subtotalMinor)}</strong>
                </div>
                {invoice.discountMinor > 0 && (
                  <div>
                    <span>DISCOUNT</span>
                    <strong>-{formatAmount(invoice.discountMinor)}</strong>
                  </div>
                )}
                <div className="grand-total">
                  <span>TOTAL</span>
                  <strong>{formatHKD(invoice.totalMinor)}</strong>
                </div>
                {invoice.paidMinor > 0 && (
                  <>
                    <div>
                      <span>PAID</span>
                      <strong>{formatHKD(invoice.paidMinor)}</strong>
                    </div>
                    <div>
                      <span>BALANCE</span>
                      <strong>{formatHKD(invoice.balanceMinor)}</strong>
                    </div>
                  </>
                )}
                {overpaidMinor > 0 && (
                  <div className="overpaid">
                    <span>OVERPAID</span>
                    <strong>{formatHKD(overpaidMinor)}</strong>
                  </div>
                )}
                <button
                  className="button secondary full"
                  type="button"
                  onClick={() => setDrawer("payments")}
                >
                  PAYMENTS / 收款 ({invoice.payments.length})
                </button>
              </div>
            </div>
          </section>

          <section className="editor-section">
            <div className="section-heading">
              <span>04</span>
              <h2>NOTES + PAYMENT</h2>
            </div>
            <div className="field-grid two">
              <Field label="CLIENT-VISIBLE NOTES">
                <textarea
                  rows={5}
                  value={invoice.clientNotes}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      clientNotes: event.target.value,
                    }))
                  }
                />
                {suggestions.clientNotes.length > 0 && (
                  <select
                    className="history-apply block"
                    aria-label="Use saved client-visible note"
                    value=""
                    onChange={(event) => {
                      if (!event.target.value) return;
                      updateInvoice((current) => ({
                        ...current,
                        clientNotes: event.target.value,
                      }));
                    }}
                  >
                    <option value="">USE SAVED VISIBLE NOTE…</option>
                    {suggestions.clientNotes.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="INTERNAL NOTES · NEVER EXPORTED">
                <textarea
                  rows={5}
                  value={invoice.internalNotes}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      internalNotes: event.target.value,
                    }))
                  }
                />
                {suggestions.internalNotes.length > 0 && (
                  <select
                    className="history-apply block"
                    aria-label="Use saved internal note"
                    value=""
                    onChange={(event) => {
                      if (!event.target.value) return;
                      updateInvoice((current) => ({
                        ...current,
                        internalNotes: event.target.value,
                      }));
                    }}
                  >
                    <option value="">USE SAVED INTERNAL NOTE…</option>
                    {suggestions.internalNotes.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="PAYMENT INSTRUCTIONS">
                <textarea
                  rows={5}
                  value={invoice.paymentInstructions}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      paymentInstructions: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="TERMS + CONDITIONS">
                <textarea
                  rows={5}
                  value={invoice.terms}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      terms: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="AUTHORISED NAME / SIGNATURE">
                <input
                  value={invoice.authorisedName}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      authorisedName: event.target.value,
                    }))
                  }
                />
              </Field>
            </div>
            <datalist id="client-notes-history">
              {suggestions.clientNotes.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
            <datalist id="internal-notes-history">
              {suggestions.internalNotes.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </section>

          <section className="editor-section receipt-section">
            <div className="section-heading">
              <span>05</span>
              <h2>RECEIPT IMAGES</h2>
              <em>EXPORTED AS APPENDIX PAGES</em>
            </div>
            <label
              className={`receipt-upload ${
                busyReceipts ||
                invoice.receipts.length >= MAX_RECEIPTS_PER_INVOICE
                  ? "disabled"
                  : ""
              }`}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                aria-label="Attach receipt images"
                disabled={
                  busyReceipts ||
                  invoice.receipts.length >= MAX_RECEIPTS_PER_INVOICE
                }
                onChange={(event) => void handleReceipts(event)}
              />
              <strong>
                {busyReceipts ? "ATTACHING…" : "＋ ATTACH RECEIPT IMAGES"}
              </strong>
              <span>
                JPG, PNG OR WEBP · 10 MB EACH ·{" "}
                {invoice.receipts.length}/{MAX_RECEIPTS_PER_INVOICE} ATTACHED
              </span>
            </label>
            <p className="receipt-export-note">
              Each image is stored privately and appears on its own
              client-visible appendix page in PDF, PNG and print exports.
            </p>
            {invoice.receipts.length > 0 && (
              <ul className="receipt-list" aria-label="Attached receipt images">
                {[...invoice.receipts]
                  .sort(
                    (a, b) =>
                      a.position - b.position ||
                      a.createdAt.localeCompare(b.createdAt),
                  )
                  .map((receipt, index) => {
                    const receiptUrl = receiptImageUrls[receipt.id];
                    return (
                      <li className="receipt-card" key={receipt.id}>
                        <div className="receipt-card-image">
                          {receiptUrl ? (
                            <img
                              src={receiptUrl}
                              alt={`Receipt ${index + 1}: ${receipt.fileName}`}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span role="status">LOADING PRIVATE IMAGE…</span>
                          )}
                        </div>
                        <div className="receipt-card-copy">
                          <strong title={receipt.fileName}>
                            {receipt.fileName}
                          </strong>
                          <span>
                            {formatReceiptFileSize(receipt.fileSize)} · PAGE{" "}
                            {invoicePageCount + index + 1}
                          </span>
                        </div>
                        <button
                          className="receipt-delete"
                          type="button"
                          aria-label={`Permanently delete receipt ${receipt.fileName}`}
                          disabled={busyReceipts}
                          onClick={() => void removeReceipt(receipt)}
                        >
                          DELETE
                        </button>
                      </li>
                    );
                  })}
              </ul>
            )}
          </section>
        </div>

        <aside
          className={`preview-pane ${mobileView !== "preview" ? "mobile-hidden" : ""}`}
        >
          <div className="preview-label">
            <span>LIVE PREVIEW</span>
            <span>
              {invoice.paperSize.toUpperCase()} ·{" "}
              {zoom === "fit" ? "FIT" : `${zoom}%`}
            </span>
          </div>
          <InvoicePreview
            invoice={invoice}
            profile={activeProfile}
            zoom={zoom}
            exportRef={previewRef}
            logoUrl={logoUrl}
            receiptImageUrls={receiptImageUrls}
          />
        </aside>
      </main>

      <div className="mobile-action-bar">
        <button type="button" onClick={() => setMobileView("editor")}>
          EDIT
        </button>
        <button type="button" onClick={() => setMobileView("preview")}>
          PREVIEW
        </button>
        <button
          className="mobile-save-button"
          type="button"
          disabled={saveState === "SAVING…"}
          aria-label="Save invoice; auto-save is on"
          onClick={saveInvoiceManually}
        >
          {saveState === "SAVING…" ? "SAVING…" : "SAVE"}
        </button>
        <button type="button" onClick={() => setDrawer("export")}>
          EXPORT
        </button>
      </div>

      <Drawer
        open={drawer === "records"}
        title="INVOICES"
        kicker="RECORDS / 記錄"
        onClose={() => setDrawer(null)}
      >
        <div className="drawer-tools">
          <Field label="SEARCH">
            <input
              type="search"
              value={recordSearch}
              onChange={(event) => setRecordSearch(event.target.value)}
              placeholder="Invoice number or client"
            />
          </Field>
          <Field label="STATUS">
            <select
              value={recordStatus}
              onChange={(event) =>
                setRecordStatus(event.target.value as typeof recordStatus)
              }
            >
              <option value="all">ALL</option>
              <option value="draft">DRAFT</option>
              <option value="sent">SENT</option>
              <option value="partially_paid">PARTIALLY PAID</option>
              <option value="paid">PAID</option>
              <option value="overdue">OVERDUE</option>
              <option value="void">VOID</option>
            </select>
          </Field>
        </div>
        <button
          className="button secondary full"
          type="button"
          onClick={() => setDrawer("history")}
        >
          TEXT HISTORY / 文字紀錄
        </button>
        {filteredRecords.length === 0 ? (
          <div className="empty-state">
            <h3>NO INVOICES YET</h3>
            <p>建立第一張 invoice，完成後可在這裡搜尋及再次使用。</p>
            <button className="button secondary" type="button" onClick={newInvoice}>
              ＋ NEW INVOICE
            </button>
          </div>
        ) : (
          <div className="record-list">
            {filteredRecords.map((record) => (
              <article key={record.id} className="record-row">
                <button
                  className="record-main"
                  type="button"
                  onClick={() => {
                    setInvoice(record);
                    setDrawer(null);
                    revisionApproved.current = false;
                  }}
                >
                  <span>
                    <strong>{record.number}</strong>
                    <small>{record.client.name || "NO CLIENT"}</small>
                  </span>
                  <span>
                    <strong>{formatHKD(record.totalMinor)}</strong>
                    <small>
                      BAL {formatHKD(record.balanceMinor)} ·{" "}
                      {formatDate(record.issueDate)}
                    </small>
                  </span>
                  <StatusMark status={record.status} />
                </button>
                <button
                  type="button"
                  className="record-delete"
                  aria-label={`Delete ${record.number}`}
                  onClick={() => void removeInvoice(record)}
                >
                  ×
                </button>
              </article>
            ))}
          </div>
        )}
      </Drawer>

      <Drawer
        open={drawer === "clients"}
        title="CLIENTS"
        kicker="CLIENT PICKER / 客戶"
        onClose={() => setDrawer(null)}
      >
        <Field label="SEARCH">
          <input
            type="search"
            value={clientSearch}
            onChange={(event) => setClientSearch(event.target.value)}
            placeholder="Business, contact or email"
          />
        </Field>
        <div className="client-list">
          {filteredClients.map((record) => (
            <div className="client-row" key={record.id}>
              <button
                type="button"
                onClick={() => {
                  setClientDraft(record.party);
                  updateInvoice((current) => ({
                    ...current,
                    client: record.party,
                  }));
                  setDrawer(null);
                }}
              >
                <strong>{record.party.name}</strong>
                <span>
                  {[record.party.contactName, record.party.email]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
              <button
                type="button"
                aria-label={`Delete ${record.party.name}`}
                onClick={() => void removeClient(record)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="drawer-section">
          <p className="eyebrow">NEW / UPDATE CLIENT</p>
          {clientDraft && (
            <PartyEditor
              legend="CLIENT DETAILS"
              party={clientDraft}
              onChange={setClientDraft}
            />
          )}
          <button
            className="button primary full"
            type="button"
            disabled={!clientDraft?.name.trim()}
            onClick={() => void saveClientRecord()}
          >
            SAVE NEW CLIENT / 確認儲存
          </button>
        </div>
      </Drawer>

      <Drawer
        open={drawer === "profiles"}
        title="BILLING PROFILES"
        kicker="IDENTITIES / 開票身份"
        onClose={() => setDrawer(null)}
      >
        <div className="profile-switch-list">
          {profiles.map((profile) => (
            <button
              className={profile.id === activeProfileId ? "active" : ""}
              type="button"
              key={profile.id}
              onClick={() => switchProfile(profile)}
            >
              <ProfileLogo
                displayName={profile.displayName}
                url={profileLogoUrls[profile.id] ?? ""}
              />
              <span className="profile-switch-copy">
                <strong>{profile.displayName}</strong>
                <small>
                  {profile.invoicePrefix} ·{" "}
                  {profile.archivedAt ? "ARCHIVED" : "ACTIVE"}
                </small>
              </span>
              <span className="profile-switch-arrow" aria-hidden="true">
                →
              </span>
            </button>
          ))}
        </div>
        <div className="new-profile-row">
          <Field label="NEW BILLING PROFILE">
            <input
              value={newProfileName}
              onChange={(event) => setNewProfileName(event.target.value)}
              placeholder="Display name"
            />
          </Field>
          <button
            className="button secondary"
            type="button"
            disabled={!newProfileName.trim()}
            onClick={() => void createAdditionalProfile()}
          >
            ＋ CREATE
          </button>
        </div>
        <div className="drawer-section profile-form">
          <p className="eyebrow">CURRENT PROFILE</p>
          <Field label="DISPLAY NAME · REQUIRED">
            <input
              value={activeProfile.displayName}
              onChange={(event) =>
                setProfiles((values) =>
                  values.map((profile) =>
                    profile.id === activeProfile.id
                      ? { ...profile, displayName: event.target.value }
                      : profile,
                  ),
                )
              }
            />
          </Field>
          <div className="field-grid two">
            <Field label="INVOICE PREFIX">
              <input
                value={activeProfile.invoicePrefix}
                onChange={(event) =>
                  setProfiles((values) =>
                    values.map((profile) =>
                      profile.id === activeProfile.id
                        ? {
                            ...profile,
                            invoicePrefix: event.target.value.toUpperCase(),
                          }
                        : profile,
                    ),
                  )
                }
              />
            </Field>
            <Field label="DEFAULT PAYMENT DAYS">
              <input
                type="number"
                min="0"
                max="365"
                value={activeProfile.defaultPaymentDays}
                onChange={(event) =>
                  setProfiles((values) =>
                    values.map((profile) =>
                      profile.id === activeProfile.id
                        ? {
                            ...profile,
                            defaultPaymentDays:
                              Number(event.target.value) || 30,
                          }
                        : profile,
                    ),
                  )
                }
              />
            </Field>
            <Field label="EMAIL">
              <input
                type="email"
                value={activeProfile.email}
                onChange={(event) =>
                  setProfiles((values) =>
                    values.map((profile) =>
                      profile.id === activeProfile.id
                        ? { ...profile, email: event.target.value }
                        : profile,
                    ),
                  )
                }
              />
            </Field>
            <Field label="PHONE">
              <input
                value={activeProfile.phone}
                onChange={(event) =>
                  setProfiles((values) =>
                    values.map((profile) =>
                      profile.id === activeProfile.id
                        ? { ...profile, phone: event.target.value }
                        : profile,
                    ),
                  )
                }
              />
            </Field>
            <Field label="WEBSITE">
              <input
                value={activeProfile.website}
                onChange={(event) =>
                  setProfiles((values) =>
                    values.map((profile) =>
                      profile.id === activeProfile.id
                        ? { ...profile, website: event.target.value }
                        : profile,
                    ),
                  )
                }
              />
            </Field>
            <Field label="REGISTRATION NO.">
              <input
                value={activeProfile.registrationNumber}
                onChange={(event) =>
                  setProfiles((values) =>
                    values.map((profile) =>
                      profile.id === activeProfile.id
                        ? {
                            ...profile,
                            registrationNumber: event.target.value,
                          }
                        : profile,
                    ),
                  )
                }
              />
            </Field>
            <Field label="BRAND COLOUR">
              <div className="colour-field">
                <input
                  type="color"
                  value={activeProfile.brandColour || "#151515"}
                  onChange={(event) =>
                    setProfiles((values) =>
                      values.map((profile) =>
                        profile.id === activeProfile.id
                          ? { ...profile, brandColour: event.target.value }
                          : profile,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  onClick={() =>
                    setProfiles((values) =>
                      values.map((profile) =>
                        profile.id === activeProfile.id
                          ? { ...profile, brandColour: "" }
                          : profile,
                      ),
                    )
                  }
                >
                  USE BLACK
                </button>
              </div>
            </Field>
            <Field label="LOGO · PNG/JPG · MAX 2MB">
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(event) => void handleLogo(event)}
              />
            </Field>
          </div>
          <Field label="ADDRESS">
            <textarea
              rows={3}
              value={activeProfile.address}
              onChange={(event) =>
                setProfiles((values) =>
                  values.map((profile) =>
                    profile.id === activeProfile.id
                      ? { ...profile, address: event.target.value }
                      : profile,
                  ),
                )
              }
            />
          </Field>
          <Field label="DEFAULT PAYMENT INSTRUCTIONS">
            <textarea
              rows={4}
              value={activeProfile.paymentInstructions}
              onChange={(event) =>
                setProfiles((values) =>
                  values.map((profile) =>
                    profile.id === activeProfile.id
                      ? {
                          ...profile,
                          paymentInstructions: event.target.value,
                        }
                      : profile,
                  ),
                )
              }
            />
          </Field>
          <div className="button-row">
            <button
              className="button primary"
              type="button"
              disabled={!activeProfile.displayName.trim()}
              onClick={() => void persistProfile(activeProfile)}
            >
              SAVE PROFILE
            </button>
            {activeProfile.archivedAt && (
              <button
                className="button secondary"
                type="button"
                onClick={() =>
                  void persistProfile({
                    ...activeProfile,
                    archivedAt: null,
                  })
                }
              >
                RESTORE
              </button>
            )}
            <button
              className="button danger"
              type="button"
              onClick={() => void removeProfile(activeProfile)}
            >
              DELETE PROFILE
            </button>
          </div>
          <p className="profile-delete-note">
            PERMANENT · Deletes this profile and all of its invoices, clients,
            payments, saved text history, and logo.
          </p>
        </div>
      </Drawer>

      <Drawer
        open={drawer === "payments"}
        title="PAYMENT RECORDS"
        kicker={`${invoice.number} / 收款`}
        onClose={() => setDrawer(null)}
      >
        <div className="payment-summary">
          <div>
            <span>TOTAL</span>
            <strong>{formatHKD(invoice.totalMinor)}</strong>
          </div>
          <div>
            <span>PAID</span>
            <strong>{formatHKD(invoice.paidMinor)}</strong>
          </div>
          <div>
            <span>BALANCE</span>
            <strong>{formatHKD(invoice.balanceMinor)}</strong>
          </div>
          {overpaidMinor > 0 && (
            <div className="overpaid">
              <span>OVERPAID</span>
              <strong>{formatHKD(overpaidMinor)}</strong>
            </div>
          )}
        </div>
        <div className="payment-list">
          {invoice.payments.map((payment, index) => (
            <div className="payment-card" key={payment.id}>
              <div className="subsection-head">
                <strong>PAYMENT {String(index + 1).padStart(2, "0")}</strong>
                <button
                  className="tertiary danger"
                  type="button"
                  onClick={() =>
                    updateInvoice((current) => ({
                      ...current,
                      payments: current.payments.filter(
                        (entry) => entry.id !== payment.id,
                      ),
                    }))
                  }
                >
                  REMOVE
                </button>
              </div>
              <div className="field-grid two">
                <Field label="AMOUNT HKD">
                  <input
                    inputMode="decimal"
                    value={(payment.amountMinor / 100).toFixed(2)}
                    onChange={(event) =>
                      updateInvoice((current) => ({
                        ...current,
                        payments: current.payments.map((entry) =>
                          entry.id === payment.id
                            ? {
                                ...entry,
                                amountMinor: majorToMinor(event.target.value),
                              }
                            : entry,
                        ),
                      }))
                    }
                  />
                </Field>
                <Field label="PAYMENT DATE">
                  <input
                    type="date"
                    value={payment.paymentDate}
                    onChange={(event) =>
                      updateInvoice((current) => ({
                        ...current,
                        payments: current.payments.map((entry) =>
                          entry.id === payment.id
                            ? { ...entry, paymentDate: event.target.value }
                            : entry,
                        ),
                      }))
                    }
                  />
                </Field>
                <Field label="METHOD">
                  <input
                    value={payment.method}
                    placeholder="Bank / FPS / PayMe"
                    onChange={(event) =>
                      updateInvoice((current) => ({
                        ...current,
                        payments: current.payments.map((entry) =>
                          entry.id === payment.id
                            ? { ...entry, method: event.target.value }
                            : entry,
                        ),
                      }))
                    }
                  />
                </Field>
                <Field label="REFERENCE">
                  <input
                    value={payment.reference}
                    onChange={(event) =>
                      updateInvoice((current) => ({
                        ...current,
                        payments: current.payments.map((entry) =>
                          entry.id === payment.id
                            ? { ...entry, reference: event.target.value }
                            : entry,
                        ),
                      }))
                    }
                  />
                </Field>
              </div>
              <Field label="PRIVATE PAYMENT NOTE">
                <textarea
                  rows={2}
                  value={payment.note}
                  onChange={(event) =>
                    updateInvoice((current) => ({
                      ...current,
                      payments: current.payments.map((entry) =>
                        entry.id === payment.id
                          ? { ...entry, note: event.target.value }
                          : entry,
                      ),
                    }))
                  }
                />
              </Field>
            </div>
          ))}
        </div>
        <button
          className="button primary full"
          type="button"
          onClick={() =>
            updateInvoice((current) => ({
              ...current,
              payments: [
                ...current.payments,
                {
                  id: createId(),
                  amountMinor: current.balanceMinor,
                  paymentDate: new Date().toISOString().slice(0, 10),
                  method: "",
                  reference: "",
                  note: "",
                },
              ],
            }))
          }
        >
          ＋ ADD PAYMENT
        </button>
      </Drawer>

      <Drawer
        open={drawer === "export"}
        title="EXPORT INVOICE"
        kicker="OUTPUT / 輸出"
        onClose={() => setDrawer(null)}
      >
        <div className="export-summary">
          <span>{invoice.number}</span>
          <strong>{formatHKD(invoice.totalMinor)}</strong>
          <small>
            {invoice.paperSize.toUpperCase()} ·{" "}
            {invoice.language === "zh-HK" ? "繁體中文" : "ENGLISH"} ·{" "}
            {invoice.items.length} ITEMS · {invoice.receipts.length} RECEIPTS
          </small>
        </div>
        <Field label="FILENAME">
          <input value={invoiceFilename(invoice)} readOnly />
        </Field>
        <div className="export-grid">
          <button
            className="export-option primary-export"
            type="button"
            disabled={busyExport || !previewRef.current}
            onClick={() => {
              if (!previewRef.current) return;
              setBusyExport(true);
              void exportInvoicePdf(previewRef.current, invoice)
                .then(() => showToast("PDF READY · MATCHES PREVIEW"))
                .catch((error: unknown) =>
                  showToast(
                    error instanceof Error ? error.message : "PDF EXPORT FAILED",
                  ),
                )
                .finally(() => setBusyExport(false));
            }}
          >
            <strong>{busyExport ? "BUILDING…" : "PDF"}</strong>
            <span>MATCHES PREVIEW · SEARCHABLE TEXT</span>
          </button>
          <button
            className="export-option"
            type="button"
            disabled={busyExport || !previewRef.current}
            onClick={() => {
              if (!previewRef.current) return;
              setBusyExport(true);
              void exportInvoicePngPages(previewRef.current, invoice, pngScale)
                .then(() => showToast("ALL PNG PAGES READY"))
                .catch(() => showToast("PNG EXPORT FAILED"))
                .finally(() => setBusyExport(false));
            }}
          >
            <strong>PNG</strong>
            <span>ALL PAGES · {pngScale}× RGB</span>
          </button>
          <button
            className="export-option"
            type="button"
            disabled={busyExport || !previewRef.current}
            onClick={() => {
              if (!previewRef.current) return;
              setBusyExport(true);
              void waitForExportImages(previewRef.current)
                .then(() => window.print())
                .catch((error: unknown) =>
                  showToast(
                    error instanceof Error
                      ? error.message
                      : "PRINT PREPARATION FAILED",
                  ),
                )
                .finally(() => setBusyExport(false));
            }}
          >
            <strong>PRINT</strong>
            <span>BROWSER PRINT DIALOG</span>
          </button>
        </div>
        <Field label="PNG RESOLUTION">
          <select
            value={pngScale}
            onChange={(event) =>
              setPngScale(Number(event.target.value) as 1 | 2)
            }
          >
            <option value={1}>1×</option>
            <option value={2}>2×</option>
          </select>
        </Field>
        <button
          className="button secondary full"
          type="button"
          onClick={() => {
            void navigator.clipboard
              .writeText(invoiceSummary(invoice))
              .then(() => showToast("SUMMARY COPIED"));
          }}
        >
          COPY SUMMARY
        </button>
        <p className="small-note">
          PDF uses the same high-resolution render as Preview and keeps a
          searchable text layer. Private Internal Notes are never included.
        </p>
      </Drawer>

      <Drawer
        open={drawer === "history"}
        title="TEXT HISTORY"
        kicker="AUTOCOMPLETE / 文字紀錄"
        onClose={() => setDrawer(null)}
      >
        <p className="small-note">
          Scoped to {activeProfile.displayName}. Only text from successfully
          saved invoices appears here. Visible and private notes remain separate.
        </p>
        {(
          [
            ["DESCRIPTION", "description", suggestions.descriptions],
            ["CLIENT-VISIBLE NOTES", "client_notes", suggestions.clientNotes],
            ["INTERNAL NOTES", "internal_notes", suggestions.internalNotes],
          ] as const
        ).map(([label, kind, values]) => (
          <section className="history-section" key={kind}>
            <div className="subsection-head">
              <h3>{label}</h3>
              <span>{values.length} ITEMS</span>
            </div>
            {values.length === 0 ? (
              <p className="history-empty">NO SAVED TEXT</p>
            ) : (
              <div className="history-list">
                {values.map((value) => (
                  <div className="history-row" key={value}>
                    <span>{value}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${value} from autocomplete history`}
                      onClick={() => void hideHistory(kind, value)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </Drawer>

      <Drawer
        open={drawer === "account"}
        title="ACCOUNT + SYNC"
        kicker="PRIVATE WORKSPACE"
        onClose={() => setDrawer(null)}
      >
        <div className="account-details">
          <div>
            <span>SIGNED IN</span>
            <strong>{signedInEmail}</strong>
          </div>
          <div>
            <span>SYNC MODE</span>
            <strong>{localMode ? "LOCAL BROWSER ONLY" : "SUPABASE / RLS"}</strong>
          </div>
          <div>
            <span>LAST SAVE</span>
            <strong>{lastSavedAt || "NOT YET SAVED"}</strong>
          </div>
          <div>
            <span>ACTIVE PROFILE</span>
            <strong>{activeProfile.displayName}</strong>
          </div>
        </div>
        {localMode ? (
          <div className="setup-callout">
            <h3>CONNECT SUPABASE</h3>
            <p>
              Copy <code>.env.example</code> to <code>.env.local</code>, add the
              new project URL and publishable key, apply the migration, then
              restart Vite. Never use a secret or service-role key here.
            </p>
          </div>
        ) : (
          <button
            className="button secondary full"
            type="button"
            onClick={() => {
              if (supabase) void supabase.auth.signOut();
            }}
          >
            SIGN OUT
          </button>
        )}
      </Drawer>

      <Toast message={toast} />
    </div>
  );
}
